import { useEffect } from 'react';

const SITE = 'https://watchsafe.app';
const DEFAULT_OG_IMAGE = `${SITE}/watchsafe-icon.svg`;
const SEO_MARK = 'data-ws-seo';

function upsertMeta(attrName, attrValue, content) {
  const sel = `meta[${attrName}="${attrValue}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    el.setAttribute(SEO_MARK, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function headlineFromTitle(title) {
  const idx = title.indexOf('|');
  return (idx === -1 ? title : title.slice(0, idx)).trim();
}

/**
 * Sets document title, meta description, canonical, Open Graph / Twitter, and optional Article JSON-LD.
 *
 * @param {{ title: string, description: string, path: string, articleSchema?: boolean, datePublished?: string, imageUrl?: string }} opts
 *   path — route path WITHOUT trailing slash, e.g. "/block-youtube-shorts"
 */
export default function useSeoMeta({
  title,
  description,
  path,
  articleSchema = false,
  datePublished,
  imageUrl,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content') || '';
    if (meta) {
      meta.setAttribute('content', description);
    } else {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      meta.setAttribute('content', description);
      document.head.appendChild(meta);
    }

    const canonicalUrl = `${SITE}${path}`;
    let link = document.querySelector('link[rel="canonical"]');
    const prevCanonical = link?.getAttribute('href') || '';
    if (link) {
      link.setAttribute('href', canonicalUrl);
    } else {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonicalUrl);
      document.head.appendChild(link);
    }

    const ogImage = imageUrl || DEFAULT_OG_IMAGE;
    upsertMeta('property', 'og:type', articleSchema ? 'article' : 'website');
    upsertMeta('property', 'og:site_name', 'WatchSafe');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:image:secure_url', ogImage);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', ogImage);

    const scriptId = 'ws-ld-page';
    if (articleSchema) {
      const headline = headlineFromTitle(title);
      const articleLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline,
        name: headline,
        description,
        url: canonicalUrl,
        image: ogImage,
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
        publisher: {
          '@type': 'Organization',
          name: 'WatchSafe',
          logo: {
            '@type': 'ImageObject',
            url: DEFAULT_OG_IMAGE,
          },
        },
      };
      if (datePublished) {
        articleLd.datePublished = datePublished;
        articleLd.dateModified = datePublished;
      }
      let scriptEl = document.getElementById(scriptId);
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.id = scriptId;
        scriptEl.setAttribute(SEO_MARK, '1');
        document.head.appendChild(scriptEl);
      } else {
        scriptEl.setAttribute(SEO_MARK, '1');
      }
      scriptEl.textContent = JSON.stringify(articleLd);
    } else {
      const existing = document.getElementById(scriptId);
      if (existing?.getAttribute(SEO_MARK) === '1') {
        existing.remove();
      }
    }

    return () => {
      document.title = prevTitle;
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute('content', prevDesc);
      const c = document.querySelector('link[rel="canonical"]');
      if (c) {
        // No canonical existed before this page set one — remove it rather than leave href="".
        if (prevCanonical) c.setAttribute('href', prevCanonical);
        else c.remove();
      }

      document.head.querySelectorAll(`meta[${SEO_MARK}="1"]`).forEach((el) => el.remove());

      const s = document.getElementById(scriptId);
      if (s?.getAttribute(SEO_MARK) === '1') {
        s.remove();
      }
    };
  }, [title, description, path, articleSchema, datePublished, imageUrl]);
}
